import {
    AppNotificationType,
    AppStoreArtifactUploadRule,
    AutoSubmitStrategy,
    AutoBackmergeStrategy,
    AutoCreateTestRunsStrategy,
    AutoReleaseStrategy,
    AutoSyncTranslationsExportStrategy,
    BuildNotesAutomationStrategy,
    AutoCherryPickStrategy,
    AutoSelectLatestBuildStrategy,
    AutomationType,
    BumpVersionAutomationStrategy,
    ProvisioningProfileType,
    PullRequestType,
    ReleaseNotesDataSource,
    RollbackResigningStrategy,
    SettingField,
    SettingFieldType,
    SettingHelpTextBehavior,
    SettingRadioDirection,
    SettingOption,
    RunwayPRAssignee,
    SyncMetadataExportTrigger,
    SyncMetadataSourceOfTruth,
    isSelectableAsBetaBuildNotesFallback,
    WorkflowType,
} from '@/types';

// Temporary hand copy of lib/autoconfig/settings_definition.go, since demo apps can't reach the backend; see runway-wiki/Demo-Apps.md
const demoUserGroupOptions: SettingOption[] = [
    { value: 'demo-engineering', label: 'Engineering' },
    { value: 'demo-qa', label: 'QA' },
    { value: 'demo-product', label: 'Product' },
];

const buildReleaseNotesSourceOptions = (defaultLabel: string): SettingOption[] => [
    { value: ReleaseNotesDataSource.DefaultReleaseNotes, label: defaultLabel },
    { value: ReleaseNotesDataSource.PreviousRelease, label: 'Pull in from previous release' },
    { value: ReleaseNotesDataSource.None, label: "Don't apply automatically" },
];

const buildWaitUntilKickedOffField = (noun: string): SettingField => ({
    key: 'shouldWaitUntilNextReleaseIsKickedOff',
    type: SettingFieldType.Checkbox,
    label: 'Sync when the current release is kicked off',
    helpText: `If this option is turned off, the sync will happen as soon as the previous version has been released. In practice, this may mean that the ${noun} may reference the next release's pilot while the previous release is still rolling out.`,
    helpTextBehavior: SettingHelpTextBehavior.Tooltip,
});

const bypassPullRequestsKey = 'shouldCommitAndPushDirectlyToTargetBranch';

const bypassPullRequestsField: SettingField = {
    key: bypassPullRequestsKey,
    type: SettingFieldType.Checkbox,
    label: 'Bypass pull requests',
    helpText:
        "If your repository has branch protection enabled for the target branch AND those branch protections include requiring pull requests to merge or restricting push access to your target branch, you'll need to update your branch protection rules to allow Runway to bypass requirements.",
    helpTextBehavior: SettingHelpTextBehavior.Tooltip,
};

const buildAssignPrToField = (
    canAssignFixOwner: boolean,
    isDisabledWhenBypassingPullRequests: boolean,
): SettingField => ({
    key: 'assignPrTo',
    type: SettingFieldType.Select,
    label: 'Pull request assignee',
    helpText: `Choose who Runway should assign to the pull requests it opens. Has no effect when the chosen user has no linked version control account.${
        isDisabledWhenBypassingPullRequests ?
            ' Unavailable while pull requests are bypassed, since Runway commits directly to the target branch instead of opening one.'
        :   ''
    }`,
    helpTextBehavior: SettingHelpTextBehavior.Tooltip,
    defaultValue: RunwayPRAssignee.None,
    options: [
        { value: RunwayPRAssignee.None, label: 'Leave PR unassigned' },
        { value: RunwayPRAssignee.ReleasePilot, label: 'Assign PR to release pilot' },
        ...(canAssignFixOwner ?
            [{ value: RunwayPRAssignee.FixOwner, label: 'Assign PR to the fix owner or code author' }]
        :   []),
    ],
    disabledWhenEquals: isDisabledWhenBypassingPullRequests ? { [bypassPullRequestsKey]: true } : undefined,
});

const isCherryPicking = { strategy: AutoBackmergeStrategy.ContinuousCherryPick };

const demoBetaBuildNotesStrategyOptions: SettingOption[] = [
    { value: BuildNotesAutomationStrategy.CarryOver, label: 'Carry over from previous beta build' },
    {
        value: BuildNotesAutomationStrategy.BuildChangelog,
        label: 'Generate changelog from diff since previous beta build',
    },
    { value: BuildNotesAutomationStrategy.CIBuildVariable, label: 'Generate notes from CI build workflow argument' },
    { value: BuildNotesAutomationStrategy.CIBuildCommitMessage, label: 'Generate notes from CI build commit message' },
    {
        value: BuildNotesAutomationStrategy.AIBuildSummary,
        label: 'Generate AI summary of diff since previous beta build',
    },
];

const demoTranslationsExportStrategyOptions: SettingOption[] = [
    { value: AutoSyncTranslationsExportStrategy.ToWorkingBranch, label: 'Working branch' },
    { value: AutoSyncTranslationsExportStrategy.ToReleaseBranch, label: 'Release branch' },
];

const demoHealthMetricOptions: SettingOption[] = [
    { value: 'demo-crash-free-users', label: 'Crash-free users' },
    { value: 'demo-crash-free-sessions', label: 'Crash-free sessions' },
];

const demoRolloutGateFields: SettingField[] = [
    {
        key: 'adoptionThresholds',
        type: SettingFieldType.NumberMap,
        label: 'Session adoption',
        min: 0,
        max: 99.9999,
    },
    {
        key: 'phasedReleaseDayThreshold',
        type: SettingFieldType.Integer,
        label: 'Phased release day',
        min: 0,
        max: 7,
    },
];

const demoRolloutHealthFields: SettingField[] = [
    ...demoRolloutGateFields,
    {
        key: 'monitoringConditions',
        type: SettingFieldType.MultiSelect,
        label: 'Health metrics',
        options: demoHealthMetricOptions,
    },
    {
        key: 'metricMaxDataAgeMinutes',
        type: SettingFieldType.NumberMap,
        label: 'Max data age',
        options: demoHealthMetricOptions,
        unitAddon: 'minutes',
    },
];

const demoUploadWorkflowTypeColumn: SettingField = {
    key: 'workflowType',
    type: SettingFieldType.Select,
    label: 'Workflow',
    options: [
        { value: WorkflowType.Staging, label: 'Release candidate workflow' },
        { value: WorkflowType.Production, label: 'Release workflow' },
    ],
};

const demoTicketStatusOptions: SettingOption[] = [
    { value: 'Done', label: 'Done' },
    { value: 'Released', label: 'Released' },
];

// Keyed by type rather than per automation, so platform-dependent fields use their iOS version
export const demoAutomationSettingFieldsByType: Partial<Record<AutomationType, SettingField[]>> = {
    [AutomationType.AutoSyncAppleDevices]: [
        {
            key: 'autoSyncAppleDevicesPossibleUserGroups',
            type: SettingFieldType.MultiSelect,
            label: 'Register devices for app users in Runway user groups',
            helpText:
                'Only devices associated with users that have one of the selected user groups will be synced. Leave blank to sync devices for all app users',
            options: demoUserGroupOptions,
        },
        {
            key: 'autoSyncAppleDevicesShouldIncludeTestDevices',
            type: SettingFieldType.Checkbox,
            label: 'Include test devices',
            helpText: 'Runway will sync devices that have been added to the app as test devices',
            defaultValue: true,
        },
        {
            key: 'autoSyncAppleDevicesShouldDisableDevices',
            type: SettingFieldType.Checkbox,
            label: 'Disable devices for users that are removed from apps in Runway',
            defaultValue: true,
        },
    ],
    [AutomationType.AutoSyncAppleProvisioningProfilesDevices]: [
        {
            key: 'autoSyncAppleProvisioningProfilesDevicesProfileTypes',
            type: SettingFieldType.MultiSelect,
            label: 'Profile types',
            helpText: 'Runway will sync the associated devices of selected profile types only',
            required: true,
            options: [
                { value: ProvisioningProfileType.IosDevelopment, label: 'iOS Development' },
                { value: ProvisioningProfileType.IosAdHoc, label: 'iOS Ad Hoc' },
            ],
        },
        {
            key: 'autoSyncAppleProvisioningProfilesDevicesUserGroups',
            type: SettingFieldType.MultiSelect,
            label: 'User groups',
            helpText:
                'Runway will only sync the devices of app users with at least one of the selected user groups. Leave blank to sync devices for all app users.',
            options: demoUserGroupOptions,
        },
        {
            key: 'autoSyncAppleProvisioningProfilesDevicesShouldRemoveDevices',
            type: SettingFieldType.Checkbox,
            label: 'Remove devices for users that are removed from apps in Runway',
            defaultValue: true,
        },
    ],
    [AutomationType.AutoSyncSlackPilotUserGroup]: [
        {
            key: 'userGroupHandle',
            type: SettingFieldType.Input,
            label: 'Release pilot handle',
            helpText:
                'Runway will automatically manage the user group for this handle, ensuring the current release pilot is its only member.',
            required: true,
            pattern: '^@?[a-zA-Z0-9_-]+$',
            patternMessage: 'use only letters (a–z), numbers (0–9), hyphens (-), and underscores (_)',
        },
        buildWaitUntilKickedOffField('handle'),
    ],
    [AutomationType.AutoSyncTeamsPilotTag]: [
        {
            key: 'tagName',
            type: SettingFieldType.Input,
            label: 'Release pilot tag',
            helpText:
                'Runway will automatically manage the Teams tag with this name, ensuring the current release pilot is its only member.',
            required: true,
        },
        buildWaitUntilKickedOffField('tag'),
    ],
    [AutomationType.AutoTagReleases]: [
        {
            key: 'fallbackToHeadIfNeeded',
            type: SettingFieldType.Checkbox,
            label: 'Allow fallback to tagging HEAD of release branch if Runway is unable to match released build to specific commit',
        },
    ],
    [AutomationType.AutoGenerateCIBuildDiffSummary]: [
        {
            key: 'showSummaryOnCIStep',
            type: SettingFieldType.Checkbox,
            label: 'Show summary on the CI step',
            defaultValue: true,
        },
        {
            key: 'notificationIdsToInclude',
            type: SettingFieldType.MultiSelect,
            label: 'Include summary in build notifications',
            options: [
                { value: AppNotificationType.BuildAvail, label: 'New build available' },
                { value: AppNotificationType.BuildAvailArtifacts, label: 'New build available with artifacts' },
            ],
        },
    ],
    [AutomationType.AutoPullSubmissionFromReview]: [
        {
            key: 'pullOnReleaseSkipped',
            type: SettingFieldType.Checkbox,
            label: 'Pull a submission from review if the release is skipped',
        },
        {
            key: 'pullOnFixOrFixRequestAccepted',
            type: SettingFieldType.Checkbox,
            label: 'Pull a submission from review if a fix is added / fix request is approved',
        },
    ],
    [AutomationType.AutoApplyMetadata]: [
        {
            key: 'releaseNotesSource',
            type: SettingFieldType.Select,
            label: "'What's new' text",
            helpText: "You can set default 'What's new' text from App settings > Flightpath > Metadata",
            defaultValue: ReleaseNotesDataSource.DefaultReleaseNotes,
            options: buildReleaseNotesSourceOptions("Use default 'What's new' text"),
        },
        {
            key: 'promoTextSource',
            type: SettingFieldType.Select,
            label: 'Promotional text',
            helpText: 'You can set default promotional text from App settings > Flightpath > Metadata',
            defaultValue: ReleaseNotesDataSource.None,
            options: buildReleaseNotesSourceOptions('Use default promotional text'),
        },
    ],
    [AutomationType.AutoDistributeReleaseSummary]: [
        {
            key: 'emailAddresses',
            type: SettingFieldType.Tags,
            label: 'Additional recipients',
            helpText:
                'Enter up to five additional recipients. Note that by default all app users will receive release summaries and can opt out independently.',
            placeholder: 'e.g. product@team.com',
        },
    ],
    [AutomationType.AutoAddMissingFAs]: [
        {
            key: 'useWorkspaceLabels',
            type: SettingFieldType.Checkbox,
            label: 'Create labels at workspace level instead of team level',
        },
    ],
    [AutomationType.AutoPromoteBuildsToTestTrack]: [
        {
            key: 'testingTrack',
            type: SettingFieldType.Input,
            label: 'Testing track',
            helpText: "Choose from 'alpha', 'beta', 'internal', or another custom testing track",
            required: true,
        },
    ],
    [AutomationType.AutoCreateRunwayReleases]: [
        {
            key: 'destinations',
            type: SettingFieldType.DestinationOptions,
            label: 'Destinations',
            isLabelHidden: true,
        },
        {
            key: 'delayUntilBackmergeMerged',
            type: SettingFieldType.Checkbox,
            label: "Wait until current release's backmerge is merged",
            helpText:
                "Do not create the new release until the current release's backmerge PR is merged. This option requires the 'Backmerge changes from the release branch' automation to be enabled.",
            helpTextBehavior: SettingHelpTextBehavior.Tooltip,
        },
    ],
    [AutomationType.AutoSyncMetadataTranslations]: [
        {
            key: 'autoSyncMetadataTranslationsBeforeSubmissionEnabled',
            type: SettingFieldType.Checkbox,
            label: 'Export translated metadata ahead of release submission',
            helpText:
                'An export will be triggered for any metadata fields in an Export needed state. Some metadata fields may still have a status of Waiting for translation when the export is triggered.',
            helpTextBehavior: SettingHelpTextBehavior.Below,
        },
        {
            key: 'daysBeforeSubmissionForExport',
            type: SettingFieldType.Integer,
            label: 'When to export',
            required: true,
            defaultValue: 1,
            min: 1,
            max: 30,
            unitAddon: 'days before submission',
            visibleWhenEquals: { autoSyncMetadataTranslationsBeforeSubmissionEnabled: true },
        },
    ],
    [AutomationType.AutoBumpVersion]: [
        {
            key: 'strategy',
            type: SettingFieldType.Select,
            label: 'Bump version strategy',
            helpText: 'Choose when Runway should bump the version for each release.',
            helpTextBehavior: SettingHelpTextBehavior.Tooltip,
            required: true,
            options: [
                {
                    value: BumpVersionAutomationStrategy.PreKickoffToCurrentVersionOnWorkingBranch,
                    label: 'Pre-kickoff, to current release version, on working branch',
                },
                {
                    value: BumpVersionAutomationStrategy.PostKickoffToCurrentVersionOnReleaseBranch,
                    label: 'Post-kickoff, to current release version, on release branch',
                },
                {
                    value: BumpVersionAutomationStrategy.PostKickoffToNextVersionOnWorkingBranch,
                    label: 'Post-kickoff, to next version, on working branch',
                },
                {
                    value: BumpVersionAutomationStrategy.PostSubmitToNextVersionOnWorkingBranch,
                    label: 'Post-submission, to next version, on working branch',
                },
                {
                    value: BumpVersionAutomationStrategy.PostReleaseToNextVersionOnWorkingBranch,
                    label: 'Post-release, to next version, on working branch',
                },
            ],
        },
        {
            key: 'skipInvalidFiles',
            type: SettingFieldType.Checkbox,
            label: 'Skip missing files',
            helpText:
                "If enabled, the bump version operation will skip any configured file that can't be fetched from the target branch, rather than failing the entire bump.",
            helpTextBehavior: SettingHelpTextBehavior.Tooltip,
        },
        bypassPullRequestsField,
        buildAssignPrToField(false, true),
    ],
    [AutomationType.AutoKickoffRelease]: [
        {
            key: 'allowKickoffWhenPreviousNotComplete',
            type: SettingFieldType.Radio,
            label: 'Allow kickoff when previous release not complete?',
            options: [
                { value: true, label: 'Yes' },
                { value: false, label: 'No' },
            ],
            radioDirection: SettingRadioDirection.Horizontal,
        },
        bypassPullRequestsField,
    ],
    [AutomationType.AutoCherryPickLocalizableStrings]: [bypassPullRequestsField, buildAssignPrToField(false, true)],
    [AutomationType.AutoBackmergeReleaseBranches]: [
        {
            key: 'strategy',
            type: SettingFieldType.Select,
            label: 'Backmerge strategy',
            defaultValue: AutoBackmergeStrategy.OnReleaseCompletion,
            options: [
                {
                    value: AutoBackmergeStrategy.OnReleaseCompletion,
                    label: 'Backmerge changes from the release branch all at once, at the end of each release cycle',
                },
                {
                    value: AutoBackmergeStrategy.Continuous,
                    label: 'Backmerge changes from the release branch continuously as they are merged',
                },
                {
                    value: AutoBackmergeStrategy.ContinuousCherryPick,
                    label: 'Cherry-pick changes from the release branch continuously as they are merged',
                },
            ],
        },
        {
            key: 'runwayPRTypesToSkip',
            type: SettingFieldType.MultiSelect,
            label: 'Runway PR types to skip',
            placeholder: 'Select Runway PR types to skip',
            options: [
                { value: PullRequestType.BumpVersion, label: 'Bump version' },
                { value: PullRequestType.PromoteCode, label: 'Promote code' },
                { value: PullRequestType.BackmergeReleasePRCommit, label: 'Continuously backmerge release branch' },
                { value: PullRequestType.CherryPickWorkingBranch, label: 'Cherry-pick from working branch' },
                { value: PullRequestType.ManualConflictResolution, label: 'Manual cherry-pick conflict resolution' },
                {
                    value: PullRequestType.ManualConflictResolutionOpenedByUser,
                    label: 'Manual cherry-pick conflict resolution (opened by user)',
                },
                { value: PullRequestType.TranslationsUpdate, label: 'Translations update' },
                { value: PullRequestType.HotfixCherryPick, label: 'Hotfix cherry-pick' },
                { value: PullRequestType.SyncMetadataToVCS, label: 'Release metadata update' },
            ],
            visibleWhenEquals: isCherryPicking,
        },
        {
            key: 'shouldBackmergeOnlyMergeCommits',
            type: SettingFieldType.Checkbox,
            label: 'Only backmerge merge commits',
            visibleWhenEquals: isCherryPicking,
        },
        {
            key: 'onlyBackmergeRunwayPRs',
            type: SettingFieldType.Checkbox,
            label: 'Only backmerge PRs created by Runway',
            visibleWhenEquals: isCherryPicking,
        },
        buildAssignPrToField(true, false),
    ],
    [AutomationType.AutoCherryPickFromWorkingBranch]: [
        {
            key: 'strategy',
            type: SettingFieldType.Select,
            label: 'Pull over fixes...',
            helpText:
                "One at a time: pull requests containing fixes against the release branch must be merged or closed before the next fix can be pulled in. As soon as they're detected: multiple pull requests containing fixes against the release branch can be open at the same time. Grouped: a single pull request containing all fixes will be open against the release branch, which you must merge manually once all fixes are in.",
            helpTextBehavior: SettingHelpTextBehavior.Tooltip,
            required: true,
            defaultValue: AutoCherryPickStrategy.SingleInSequence,
            options: [
                {
                    value: AutoCherryPickStrategy.SingleInSequence,
                    label: '...one at a time in the order in which they were merged',
                },
                {
                    value: AutoCherryPickStrategy.SingleInParallel,
                    label: "...in the order in which they were merged, as soon as they're detected",
                },
                {
                    value: AutoCherryPickStrategy.Cumulative,
                    label: "...by grouping all fixes into a single PR, in the order in which they were merged, as soon as they're detected",
                },
            ],
        },
        {
            key: 'requiresFixApprovalToCherryPick',
            type: SettingFieldType.Checkbox,
            label: 'Require fix approval before performing cherry-pick',
            helpText:
                "Fixes must be approved in Runway before they can be automatically cherry-picked. 'Fix approvals' must be enabled from App settings > General > Fixes.",
            helpTextBehavior: SettingHelpTextBehavior.Tooltip,
        },
        {
            key: 'shouldNotifyPRAuthors',
            type: SettingFieldType.Checkbox,
            label: 'Notify the original author when a PR has been cherry-picked',
        },
        {
            key: 'skipPostingPRCommentsOnCherryPickSuccess',
            type: SettingFieldType.Checkbox,
            label: 'Skip posting PR comments on cherry-pick success',
            helpText:
                'By default Runway comments on the original pull-request once its commits have been cherry-picked. Check this to stop posting that comment.',
            helpTextBehavior: SettingHelpTextBehavior.Tooltip,
        },
        {
            key: 'skipPostingPRCommentsOnCherryPickMergeConflicts',
            type: SettingFieldType.Checkbox,
            label: 'Skip posting PR comments on cherry-pick merge conflicts',
            helpText:
                'By default Runway comments on the original pull-request when a cherry-pick hits a merge conflict. Check this to stop posting that comment.',
            helpTextBehavior: SettingHelpTextBehavior.Tooltip,
        },
        buildAssignPrToField(true, false),
    ],
    [AutomationType.AutoAssignBetaBuilds]: [
        {
            key: 'selectedBetaGroupIds',
            type: SettingFieldType.MultiSelect,
            label: 'Default beta testing groups',
            required: true,
            options: [
                { value: 'demo-internal-testers', label: 'Internal testers' },
                { value: 'demo-external-testers', label: 'External testers' },
            ],
        },
    ],
    [AutomationType.AutoSelectLatestBuild]: [
        {
            key: 'strategy',
            type: SettingFieldType.Select,
            label: 'Build selection preference',
            required: true,
            defaultValue: AutoSelectLatestBuildStrategy.LatestAvailableBuild,
            options: [
                {
                    value: AutoSelectLatestBuildStrategy.LatestAvailableBuild,
                    label: 'Select the latest build available on TestFlight for the release version',
                },
                {
                    value: AutoSelectLatestBuildStrategy.LatestPassedRegression,
                    label: 'Select the latest build that passed regression testing for the release version',
                },
            ],
        },
        {
            key: 'regressionStepsToConsider',
            type: SettingFieldType.MultiSelect,
            label: 'Regression test step(s) to include',
            helpText: 'The selected step(s) must all have a passing build in order for the build to be auto-selected.',
            helpTextBehavior: SettingHelpTextBehavior.Tooltip,
            placeholder: 'No regression test steps selected',
            required: true,
            defaultValue: ['demo-regression-testing'],
            options: [{ value: 'demo-regression-testing', label: 'Regression testing' }],
            visibleWhenEquals: { strategy: AutoSelectLatestBuildStrategy.LatestPassedRegression },
        },
        {
            key: 'shouldRequireBuildMatch',
            type: SettingFieldType.Checkbox,
            label: 'Only run this automation when there is a match between an App store build and CI build',
        },
    ],
    [AutomationType.AutoApplyLabelsToFixTickets]: [
        {
            key: 'fixLabels',
            type: SettingFieldType.Tags,
            label: 'Label(s) to apply to tickets associated with fixes',
            requiredUnlessAnySet: ['applyApprovedLabels', 'applyRejectedLabels'],
        },
        {
            key: 'applyApprovedLabels',
            type: SettingFieldType.Checkbox,
            label: 'Apply additional labels when a fix is approved',
        },
        {
            key: 'approvedLabels',
            type: SettingFieldType.Tags,
            label: 'Labels to apply when a fix is approved',
            isLabelHidden: true,
            required: true,
            visibleWhenEquals: { applyApprovedLabels: true },
        },
        {
            key: 'applyRejectedLabels',
            type: SettingFieldType.Checkbox,
            label: 'Apply additional labels when a fix is rejected',
        },
        {
            key: 'rejectedLabels',
            type: SettingFieldType.Tags,
            label: 'Labels to apply when a fix is rejected',
            isLabelHidden: true,
            required: true,
            visibleWhenEquals: { applyRejectedLabels: true },
        },
    ],
    [AutomationType.AutoApplyBetaBuildNotes]: [
        {
            key: 'strategy',
            type: SettingFieldType.Select,
            label: 'Beta notes source',
            required: true,
            defaultValue: BuildNotesAutomationStrategy.CarryOver,
            options: demoBetaBuildNotesStrategyOptions,
        },
        {
            key: 'ciBuildVariableKey',
            type: SettingFieldType.Input,
            label: 'CI build workflow argument',
            required: true,
            visibleWhenEquals: { strategy: BuildNotesAutomationStrategy.CIBuildVariable },
        },
        ...[
            BuildNotesAutomationStrategy.CIBuildVariable,
            BuildNotesAutomationStrategy.CIBuildCommitMessage,
            BuildNotesAutomationStrategy.AIBuildSummary,
            BuildNotesAutomationStrategy.AIReleaseSummary,
        ].map((primary) => ({
            key: 'fallbackStrategy',
            type: SettingFieldType.Select,
            label: 'Fallback beta notes source',
            defaultValue: BuildNotesAutomationStrategy.CarryOver,
            options: demoBetaBuildNotesStrategyOptions.filter((option) =>
                isSelectableAsBetaBuildNotesFallback(option.value, primary),
            ),
            visibleWhenEquals: { strategy: primary },
        })),
    ],
    [AutomationType.AutoCreatePMTicketOnTriageIssueFixRequest]: [
        {
            key: 'jiraProjectKey',
            type: SettingFieldType.Select,
            label: 'Jira project',
            helpText: 'New triage issues will create a ticket in this Jira project.',
            placeholder: 'Select a project',
            required: true,
            options: [{ value: 'MOB', label: 'Mobile (MOB)' }],
        },
    ],
    [AutomationType.AutoCreateTestRuns]: [
        {
            key: 'strategy',
            type: SettingFieldType.Radio,
            label: 'Create test runs',
            isLabelHidden: true,
            required: true,
            defaultValue: AutoCreateTestRunsStrategy.WithoutTestPlans,
            options: [
                { value: AutoCreateTestRunsStrategy.WithoutTestPlans, label: 'Without test plans' },
                { value: AutoCreateTestRunsStrategy.WithTestPlans, label: 'With test plans' },
            ],
        },
        {
            key: 'testRunsFilterSettings',
            type: SettingFieldType.Rows,
            label: 'Test run filters',
            isLabelHidden: true,
            required: true,
            visibleWhenEquals: { strategy: AutoCreateTestRunsStrategy.WithoutTestPlans },
        },
        {
            key: 'testPlansFilterSettings',
            type: SettingFieldType.Rows,
            label: 'Test plan filters',
            isLabelHidden: true,
            required: true,
            visibleWhenEquals: { strategy: AutoCreateTestRunsStrategy.WithTestPlans },
        },
    ],
    [AutomationType.AutoRelease]: [
        {
            key: 'strategy',
            type: SettingFieldType.Select,
            label: 'Release',
            required: true,
            defaultValue: AutoReleaseStrategy.OnDate,
            options: [
                { value: AutoReleaseStrategy.OnDate, label: 'On or after target date' },
                {
                    value: AutoReleaseStrategy.OnceStepsCompleteAfterTargetDate,
                    label: 'On or after target date, if all previous steps are ready',
                },
                {
                    value: AutoReleaseStrategy.OnceStepsComplete,
                    label: 'As soon as possible, if all previous steps are ready',
                },
            ],
        },
        {
            key: 'shouldUseCustomRequiredSteps',
            type: SettingFieldType.Checkbox,
            label: 'Override default required steps',
        },
        {
            key: 'customRequiredSteps',
            type: SettingFieldType.RequiredSteps,
            label: 'Required steps',
            isLabelHidden: true,
            required: true,
            visibleWhenEquals: { shouldUseCustomRequiredSteps: true },
        },
        {
            key: 'shouldReleasePreviousSubmissionToAllUsersFirst',
            type: SettingFieldType.Checkbox,
            label: 'Accelerate previous release to 100% of users if needed before releasing new version',
            helpText:
                'Standard releases only. By default, your prior release will NOT automatically accelerate to 100% when releasing the next version. With multiple subsequent incomplete rollouts, this may mean that some of your users will continue on older (n-2, n-3) versions even if they have enabled automatic updates. Choose this option to ensure all your users will be on at least your prior release when they update. Releases that were previously halted, or releases before a rollback or hotfix, will not be accelerated to 100% automatically.',
            helpTextBehavior: SettingHelpTextBehavior.Tooltip,
            disabledWhenEquals: { shouldWaitForPreviousReleaseToBeFullyRolledOut: true },
        },
        {
            key: 'delayAfterAcceleratingPreviousReleaseMinutes',
            type: SettingFieldType.DurationMinutes,
            label: 'Delay release by',
            helpText: 'Count begins from the moment Runway accelerates the previous release.',
            helpTextBehavior: SettingHelpTextBehavior.Tooltip,
            min: 0,
            max: 1440,
            visibleWhenEquals: { shouldReleasePreviousSubmissionToAllUsersFirst: true },
            isIndented: true,
        },
        {
            key: 'shouldWaitForPreviousReleaseToBeFullyRolledOut',
            type: SettingFieldType.Checkbox,
            label: 'Do not release until the previous release is 100% rolled out, or has been halted/paused',
            disabledWhenEquals: { shouldReleasePreviousSubmissionToAllUsersFirst: true },
        },
    ],
    [AutomationType.AutoSyncMetadataFromVCS]: [
        {
            key: 'sourceOfTruth',
            type: SettingFieldType.Radio,
            label: 'Release metadata source',
            required: true,
            defaultValue: SyncMetadataSourceOfTruth.Vcs,
            options: [
                { value: SyncMetadataSourceOfTruth.Vcs, label: 'Files in repo' },
                { value: SyncMetadataSourceOfTruth.Runway, label: 'Runway' },
            ],
        },
        {
            key: 'exportTrigger',
            type: SettingFieldType.Radio,
            label: 'Commit metadata changes',
            required: true,
            defaultValue: SyncMetadataExportTrigger.OnApproval,
            radioDirection: SettingRadioDirection.Horizontal,
            visibleWhenEquals: { sourceOfTruth: SyncMetadataSourceOfTruth.Runway },
            options: [
                { value: SyncMetadataExportTrigger.OnApproval, label: 'When the metadata step is approved' },
                { value: SyncMetadataExportTrigger.OnSave, label: 'Whenever metadata changes' },
            ],
        },
        {
            key: 'exportTarget',
            type: SettingFieldType.Select,
            label: 'Open pull request against...',
            required: true,
            defaultValue: AutoSyncTranslationsExportStrategy.ToReleaseBranch,
            visibleWhenEquals: { sourceOfTruth: SyncMetadataSourceOfTruth.Runway },
            options: demoTranslationsExportStrategyOptions,
        },
        {
            key: 'autoSyncMetadataFromVCSFilePath',
            type: SettingFieldType.Input,
            label: 'Metadata file path',
            placeholder: 'ProjectName/metadata',
            required: true,
        },
    ],
    [AutomationType.AutoSyncTranslations]: [
        {
            key: 'exportStrategy',
            type: SettingFieldType.Select,
            label: 'Export translations to the',
            required: true,
            defaultValue: AutoSyncTranslationsExportStrategy.ToReleaseBranch,
            options: demoTranslationsExportStrategyOptions,
        },
        {
            key: 'autoSyncTranslationsBeforeKickoffEnabled',
            type: SettingFieldType.Checkbox,
            label: 'Upload localizable strings for translation ahead of release kickoff',
        },
        {
            key: 'daysBeforeKickoffForUpload',
            type: SettingFieldType.Integer,
            label: 'When to upload',
            required: true,
            defaultValue: 1,
            min: 1,
            max: 30,
            unitAddon: 'days before kickoff',
            visibleWhenEquals: { autoSyncTranslationsBeforeKickoffEnabled: true },
        },
        {
            key: 'autoSyncTranslationsBeforeSubmissionEnabled',
            type: SettingFieldType.Checkbox,
            label: 'Export translations from translations provider ahead of release submission',
        },
        {
            key: 'daysBeforeSubmissionForExport',
            type: SettingFieldType.Integer,
            label: 'When to export',
            required: true,
            defaultValue: 1,
            min: 1,
            max: 30,
            unitAddon: 'days before submission',
            visibleWhenEquals: { autoSyncTranslationsBeforeSubmissionEnabled: true },
        },
    ],
    [AutomationType.AutoTransitionIssueTrackingTickets]: [
        {
            key: 'milestoneStatusOptions',
            type: SettingFieldType.MilestoneStatuses,
            label: 'Status updates',
            helpText:
                'For each release milestone, select the statuses associated tickets can move to. Each ticket uses a status available in its project.',
            helpTextBehavior: SettingHelpTextBehavior.Tooltip,
            required: true,
            options: demoTicketStatusOptions,
        },
        {
            key: 'transitionNewTicketsAfterKickoff',
            type: SettingFieldType.Checkbox,
            label: 'Also update status of new tickets added to the release after kickoff',
            visibleWhenAnySet: ['milestoneStatusOptions.kickoff'],
        },
    ],
    [AutomationType.AutoApplyReviewAttachments]: [
        {
            key: 'reviewAttachmentFile',
            type: SettingFieldType.FileAttachment,
            label: 'Review attachment',
            required: true,
        },
    ],
    [AutomationType.AutoCreateIncidents]: [
        {
            key: 'reviewRejectionTrigger.enabled',
            type: SettingFieldType.Checkbox,
            label: 'Submission is rejected during app store review',
        },
        {
            key: 'rolloutHaltedTrigger.enabled',
            type: SettingFieldType.Checkbox,
            label: 'An active phased release is halted',
        },
        {
            key: 'unhealthyMetricsTrigger.enabled',
            type: SettingFieldType.Checkbox,
            label: 'One or more health metrics become unhealthy',
        },
        ...demoRolloutGateFields.map((field) => ({
            ...field,
            key: `unhealthyMetricsTrigger.${field.key}`,
            visibleWhenEquals: { 'unhealthyMetricsTrigger.enabled': true },
        })),
        {
            key: 'unhealthyMetricsTrigger.monitoringConditions',
            type: SettingFieldType.MultiSelect,
            label: 'Health metrics',
            required: true,
            options: demoHealthMetricOptions,
            visibleWhenEquals: { 'unhealthyMetricsTrigger.enabled': true },
        },
    ],
    [AutomationType.AutoCreateReleaseChannels]: [
        {
            key: 'archiveChannelsWhenReleaseCompleted',
            type: SettingFieldType.Checkbox,
            label: 'Archive release channels when the release rollout is complete',
            helpText:
                'Release-specific channels will be automatically archived once the release is rolled out to 100% of users.',
            helpTextBehavior: SettingHelpTextBehavior.Tooltip,
        },
        { key: 'inviteUsers', type: SettingFieldType.MultiSelect, label: 'Auto-add users', placeholder: 'Select' },
        {
            key: 'inviteGroups',
            type: SettingFieldType.MultiSelect,
            label: 'Auto-add user groups',
            placeholder: 'Select',
            options: demoUserGroupOptions,
        },
    ],
    [AutomationType.AutoHaltUnstablePhasedReleases]: demoRolloutHealthFields,
    [AutomationType.AutoPromoteStablePhasedReleases]: [
        ...demoRolloutHealthFields,
        {
            key: 'preconditionMetrics',
            type: SettingFieldType.MultiSelect,
            label: 'Precondition health metrics',
            options: demoHealthMetricOptions,
        },
        {
            key: 'checklistItems',
            type: SettingFieldType.MultiSelect,
            label: 'Require checklist item completion for...',
        },
    ],
    [AutomationType.AutoPrepareRollbackRelease]: [
        {
            key: 'strategy',
            type: SettingFieldType.Select,
            label: 'Re-signing strategy',
            required: true,
            defaultValue: RollbackResigningStrategy.LocalResign,
            options: [
                { value: RollbackResigningStrategy.LocalResign, label: 'Re-sign by Runway' },
                { value: RollbackResigningStrategy.CIResign, label: 'Rebuild by your CI' },
            ],
        },
        {
            key: 'shouldAutoSubmit',
            type: SettingFieldType.Checkbox,
            label: 'Submit automatically when all rollback release steps are ready (green)',
        },
        ...demoRolloutGateFields.map((field) => ({
            ...field,
            key: `abandonRollback${field.key[0].toUpperCase()}${field.key.slice(1)}`,
        })),
        {
            key: 'abandonRollbackMonitoringConditions',
            type: SettingFieldType.MultiSelect,
            label: 'Health metrics',
            options: demoHealthMetricOptions,
        },
    ],
    [AutomationType.AutoStagedRollout]: [
        {
            key: 'autoStagedRolloutBlackoutTimezone',
            type: SettingFieldType.Select,
            label: 'Timezone',
            helpText: 'Choose the timezone for your blackout periods.',
        },
        {
            key: 'autoStagedRolloutBlackoutSegmentsByDay',
            type: SettingFieldType.BlackoutSchedule,
            label: 'Blackout days',
        },
    ],
    [AutomationType.AutoSubmitRelease]: [
        {
            key: 'submitStrategy',
            type: SettingFieldType.Select,
            label: 'Submit for review:',
            required: true,
            defaultValue: AutoSubmitStrategy.OnTargetDate,
            options: [
                {
                    value: AutoSubmitStrategy.AsSoonAsPossible,
                    label: 'As soon as possible',
                    helpText:
                        'Runway will submit for review as soon as an uploaded build is detected and all other submission requirements are met.',
                },
                {
                    value: AutoSubmitStrategy.AfterTargetDateEvenIfNotReady,
                    label: 'On the target date',
                    helpText:
                        'Runway will submit for review on the target date without requiring previous steps be ready.',
                },
                {
                    value: AutoSubmitStrategy.OnceStepsComplete,
                    label: 'As soon as possible, if all previous steps are ready',
                },
                {
                    value: AutoSubmitStrategy.OnTargetDate,
                    label: 'On the target date, if all previous steps are ready',
                },
                {
                    value: AutoSubmitStrategy.OnceStepsCompleteAfterTargetDate,
                    label: 'On or after target date, if all previous steps are ready',
                },
            ],
        },
    ],
    [AutomationType.AutoTriggerCIWorkflow]: [
        {
            key: 'skipIfBuildAlreadyExists',
            type: SettingFieldType.Checkbox,
            label: 'Skip execution if a CI build has already been triggered for version',
        },
    ],
    [AutomationType.AutoUploadBinaryArtifactToAppStore]: [
        {
            key: 'uploadRule',
            type: SettingFieldType.Select,
            label: 'Upload behavior for multiple available builds',
            placeholder: 'Select an upload behavior',
            required: true,
            defaultValue: AppStoreArtifactUploadRule.UploadNewestBuildOnly,
            options: [
                {
                    value: AppStoreArtifactUploadRule.ContinueOnFailure,
                    label: 'Upload artifacts for all builds – continue uploads on failure',
                },
                {
                    value: AppStoreArtifactUploadRule.StopOnFailure,
                    label: 'Upload artifacts for all builds – stop uploads on failure',
                },
                {
                    value: AppStoreArtifactUploadRule.UploadNewestBuildOnly,
                    label: 'Upload artifacts for newest build only',
                },
            ],
        },
        {
            key: 'uploadWorkflowSettings',
            type: SettingFieldType.Rows,
            label: 'Upload rules',
            isLabelHidden: true,
            required: true,
            itemLabel: 'rule',
            columns: [
                demoUploadWorkflowTypeColumn,
                { key: 'fileNames', type: SettingFieldType.Tags, label: 'File name', required: true },
                {
                    key: 'destinationInstallationId',
                    type: SettingFieldType.Select,
                    label: 'Destination',
                    required: true,
                },
                { key: 'trackName', type: SettingFieldType.Input, label: 'Track' },
                {
                    key: 'createNewTrackReleaseWithPatternedName',
                    type: SettingFieldType.Input,
                    label: 'Create new track release with name',
                },
            ],
        },
    ],
    [AutomationType.AutoUploadDsymsToStabilityMonitoring]: [
        {
            key: 'uploadWorkflowSettings',
            type: SettingFieldType.Rows,
            label: 'Upload rules',
            isLabelHidden: true,
            required: true,
            itemLabel: 'rule',
            columns: [
                { ...demoUploadWorkflowTypeColumn, required: true },
                { key: 'fileNames', type: SettingFieldType.Tags, label: 'File name', required: true },
                { key: 'stabilityMonitoringInstallationIds', type: SettingFieldType.MultiSelect, label: 'Upload to' },
            ],
        },
    ],
};
